import { useState, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/useAuth';

export interface PricingTier {
  id: string;
  line_item_id: string;
  qty_min: number;
  qty_max?: number;
  unit_cost: number;
}

export interface LineItemRow {
  id: string;
  item_type: 'internal' | 'system';
  name: string;
  title?: string;
  description?: string;
  base_unit: string;
  base_cost: number;
  final_price?: number | null;
  markup_percentage?: number;
  markup_source?: string;
  override_markup?: boolean;
  custom_markup?: number;
  image_urls?: string[];
  category_id?: string | null;
  category_name?: string | null;
  trade_id?: string | null;
  trade_name?: string | null;
  unit_symbol?: string;
  browse_category_id?: string | null;
  browse_category_name?: string | null;
  tags?: string[] | null;
  is_active: boolean;
  price_last_updated_at?: string | null;
  updated_at: string;
  created_at: string;
  notes?: string | null;
  default_markup_percent?: number | null;
  conversion_unit?: string | null;
  conversion_factor?: number | null;
  productivity_unit?: string | null;
  productivity_rate?: number | null;
  // For dialog compatibility — internal items only
  internal_pricing_tiers: PricingTier[];
  // Legacy free-text fields for filter builder compatibility
  category?: string | null;
  trade?: string | null;
  total_count?: number;
  company_id: string;
  created_by: string;
  supplier_id: string | null;
  supplier_name: string | null;
}

const PAGE_SIZE = 50;

export const useInternalLineItemData = () => {
  const { profile } = useAuth() as any;
  const companyId = profile?.company_id ?? null;
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [itemTypes, setItemTypes] = useState<string[] | null>(null);
  // null = all types
  const [browseCategoryId, setBrowseCategoryId] = useState<string | null>(null);

  // Debounce search 250ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  const vatQuery = useQuery({
    queryKey: ['company_vat', companyId],
    queryFn: async () => {
      if (!companyId) return { is_vat_registered: false, vat_rate: 15 };
      const { data, error } = await supabase
        .from('companies')
        .select('is_vat_registered, "VAT Rate"')
        .eq('id', companyId)
        .single();
      if (error || !data) return { is_vat_registered: false, vat_rate: 15 };
      return {
        is_vat_registered: data.is_vat_registered ?? false,
        vat_rate: (data as any)['VAT Rate'] ?? 15,
      };
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  });

  const queryKey = [
    'line_items_unified',
    companyId,
    debouncedSearch,
    sortBy,
    sortDir,
    page,
    itemTypes,
    browseCategoryId,
    vatQuery.data?.is_vat_registered,
    vatQuery.data?.vat_rate,
  ];

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<LineItemRow[]> => {
      if (!companyId) return [];
      const { data, error } = await (supabase.rpc as any)(
        'search_line_items',
        {
          p_company_id: companyId,
          p_search: debouncedSearch || null,
          p_item_types: itemTypes,
          p_browse_category_id: browseCategoryId,
          p_sort_by: sortBy,
          p_sort_dir: sortDir,
          p_page: page,
          p_page_size: PAGE_SIZE,
          p_is_vat_registered: vatQuery.data?.is_vat_registered ?? false,
          p_company_vat_rate: vatQuery.data?.vat_rate ?? 15,
        }
      );
      if (error) throw error;
      // Normalise: map category_name/trade_name to legacy
      // category/trade fields so useInternalLineItemFilterBuilder
      // continues to work for internal items
      return ((data as LineItemRow[]) ?? []).map(item => ({
        ...item,
        title: item.name,
        category: item.category_name ?? null,
        trade: item.trade_name ?? null,
        internal_pricing_tiers: item.internal_pricing_tiers ?? [],
        supplier_id: (item as any).supplier_id ?? null,
        supplier_name: (item as any).supplier_name ?? null,
      }));
    },
    enabled: !!companyId,
    staleTime: 30 * 1000,
  });

  const fetchLineItems = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['line_items_unified', companyId] });
  }, [queryClient, companyId]);

  const lineItems = query.data ?? [];
  const totalCount = lineItems[0]?.total_count ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const isLoading = query.isLoading;
  const error = query.error ? String(query.error) : null;

  return {
    lineItems,
    fetchLineItems,
    search,
    setSearch,
    sortBy,
    setSortBy,
    sortDir,
    setSortDir,
    isLoading,
    error,
    // New additions — consumers can opt in
    page,
    setPage,
    totalCount,
    totalPages,
    pageSize: PAGE_SIZE,
    itemTypes,
    setItemTypes,
    browseCategoryId,
    setBrowseCategoryId,
  };
};
